// Resolve the canonical OCR X-User-Id once per session.
//
// On mount, picks orgId + username out of the Keycloak token (with
// optional env overrides for staging) and calls
// `POST /document_extraction/account`. The returned id is cached at
// module scope so the axios interceptor can attach it to every
// subsequent request without juggling promises.
//
// Mount this hook on top-level OCR routes (OcrTrackingPage,
// VerificationCockpitPage). Calling it from multiple places is safe —
// react-query dedupes by key.

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { ocrApi, setResolvedXUserId } from "../../services/ocrApi";
import { useAuthContext } from "../../components/auth/AuthProvider";

const OIDC_STORAGE_KEY =
  "oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app";

const ENV_USERNAME =
  (import.meta.env.VITE_OCR_USERNAME as string | undefined) ?? "";
const ENV_ORG_ID =
  (import.meta.env.VITE_OCR_ORG_ID_OVERRIDE as string | undefined) ?? "";

interface ResolverInputs {
  orgId: string;
  username: string;
}

/**
 * Why a resolve attempt didn't run. Surfaced on the hook result so callers
 * (and the toast effect below) can react with a clear message instead of a
 * silent no-op.
 */
export type ResolverGate =
  | { state: "not-authenticated" }
  | { state: "missing-claims"; missingOrgId: boolean; missingUsername: boolean }
  | { state: "ready"; inputs: ResolverInputs };

/**
 * Inspect the OIDC profile and decide whether we have everything needed to
 * call `/document_extraction/account`. Returns a discriminated union so the
 * UI can distinguish between "still signing in", "org mapping missing", and
 * "ready to resolve".
 */
function diagnoseResolverInputs(
  user: ReturnType<typeof useAuthContext>["user"],
): ResolverGate {
  const profile = user?.profile as
    | (Record<string, unknown> & { preferred_username?: string })
    | undefined;
  if (!profile) return { state: "not-authenticated" };

  const username = ENV_USERNAME || profile.preferred_username || "";
  // org_id is an array on doit Keycloak; we use the first.
  const rawOrgs = profile["org_id"];
  let orgFromToken: string | null = null;
  if (Array.isArray(rawOrgs) && rawOrgs.length > 0) {
    orgFromToken = String(rawOrgs[0]);
  } else if (typeof rawOrgs === "string" && rawOrgs) {
    orgFromToken = rawOrgs;
  }
  const orgId = ENV_ORG_ID || orgFromToken || "";

  if (orgId && username) {
    return { state: "ready", inputs: { orgId, username } };
  }
  return {
    state: "missing-claims",
    missingOrgId: !orgId,
    missingUsername: !username,
  };
}

// Toast id used to dedupe the "org mapping missing" alert across multiple
// mounts (both OcrTrackingPage and VerificationCockpitPage call this hook;
// without a stable id sonner would stack two identical toasts).
const ORG_MISSING_TOAST_ID = "ocr-org-mapping-missing";
const USERNAME_MISSING_TOAST_ID = "ocr-username-mapping-missing";

export interface UseResolveOcrAccountResult {
  xUserId: string | null;
  /** True while the resolve call is in flight. */
  isResolving: boolean;
  /** Set when the resolve has failed; the rest of the page can degrade. */
  error: Error | null;
  /** Why the resolve didn't run, when applicable. Null = ready / resolved. */
  gate: ResolverGate;
}

export function useResolveOcrAccount(): UseResolveOcrAccountResult {
  const auth = useAuthContext();
  const gate = diagnoseResolverInputs(auth.user);
  const inputs = gate.state === "ready" ? gate.inputs : null;
  const missingOrgId =
    gate.state === "missing-claims" && gate.missingOrgId;
  const missingUsername =
    gate.state === "missing-claims" && gate.missingUsername;

  // IAM lookup for the human-readable org name.
  // GET /iam/api/organizations/{orgId} -> { id, name, displayName, ... }
  // Reads the Bearer token straight off the AuthProvider user rather
  // than going through organizationService (which pulls the token from
  // localStorage under a specific OIDC key and 401s when oidc-client-ts
  // stores it in sessionStorage instead -- same failure mode RBACContext
  // avoids by using auth.user.access_token directly).
  const accessToken = auth.user?.access_token || "";
  const orgNameQuery = useQuery({
    queryKey: ["iam", "organization", inputs?.orgId],
    queryFn: async () => {
      const res = await fetch(`/iam/api/organizations/${inputs!.orgId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        throw new Error(
          `IAM organizations lookup failed (${res.status} ${res.statusText})`,
        );
      }
      const org = (await res.json()) as { name?: string; displayName?: string };
      return org?.name || org?.displayName || "";
    },
    enabled: !!inputs?.orgId && !!accessToken,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const resolvedOrgName = orgNameQuery.data || "";

  // Gate the /account POST on the IAM lookup settling (success or
  // error). `isFetched` is stable -- it flips true after the first
  // completion and stays there -- unlike `isFetching` which toggles
  // true->false mid-render and made the gate flap. The flapping was
  // triggering react-query to cancel the in-flight /account call and
  // then re-fire it, which showed up as "(canceled)" account requests
  // in DevTools right before the real one.
  const iamSettled = !inputs?.orgId || orgNameQuery.isFetched;
  const resolveEnabled = !!inputs && iamSettled;

  const query = useQuery({
    // Key includes orgName so an org rename (or the async IAM answer
    // arriving after a re-mount) triggers a re-resolve.
    queryKey: ["ocr", "account", inputs?.orgId, inputs?.username, resolvedOrgName],
    queryFn: () => {
      // Inputs guaranteed non-null here because `enabled` gates it; the
      // localStorage fallback below covers the brief window where the
      // auth context lags behind a hydrated token.
      if (inputs) {
        return ocrApi.resolveAccount({ ...inputs, orgName: resolvedOrgName });
      }

      const raw = localStorage.getItem(OIDC_STORAGE_KEY);
      if (!raw) throw new Error("Not signed in (no OIDC user in storage)");
      const parsed = JSON.parse(raw) as { profile?: Record<string, unknown> };
      const fallbackGate = diagnoseResolverInputs({
        profile: parsed.profile,
      } as ReturnType<typeof useAuthContext>["user"]);
      if (fallbackGate.state !== "ready") {
        throw new Error(
          "Can't resolve OCR account — Keycloak token missing org_id or preferred_username",
        );
      }
      // localStorage-hydrate path doesn't have the IAM query result on
      // hand, but resolvedOrgName is closed over from this render so
      // it still ships the right value once the IAM lookup lands.
      return ocrApi.resolveAccount({
        ...fallbackGate.inputs,
        orgName: resolvedOrgName,
      });
    },
    enabled: resolveEnabled,
    staleTime: Infinity, // session-scoped — re-resolve only on inputs change
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Push the result into the module-level cache so the request
  // interceptor (which is sync) can read it.
  useEffect(() => {
    if (query.data?.xUserId) {
      setResolvedXUserId(query.data.xUserId);
    }
    return () => {
      // NOTE: we do NOT clear on unmount — the cache lives session-wide
      // and other OCR pages may be relying on it.
    };
  }, [query.data?.xUserId]);

  // Surface "user-org mapping not configured" as a persistent toast. The
  // OCR backend gates every call on a known {orgId, username} pair, so a
  // missing org_id claim means nothing in the OCR section of the app will
  // work until the user's account is mapped to an organization.
  useEffect(() => {
    if (missingOrgId) {
      toast.error("Your account isn't mapped to an organization", {
        id: ORG_MISSING_TOAST_ID,
        description:
          "User-org mapping hasn't been configured yet, so OCR features can't be loaded. Please ask your administrator to map your account to an organization, then sign in again.",
        duration: Infinity, // keep visible until dismissed or fixed
      });
    } else {
      toast.dismiss(ORG_MISSING_TOAST_ID);
    }
  }, [missingOrgId]);

  // Same treatment for the (rarer) case where preferred_username is missing.
  useEffect(() => {
    if (missingUsername) {
      toast.error("Your account is missing a username", {
        id: USERNAME_MISSING_TOAST_ID,
        description:
          "The signed-in user has no `preferred_username` claim, so OCR features can't be loaded. Please contact your administrator.",
        duration: Infinity,
      });
    } else {
      toast.dismiss(USERNAME_MISSING_TOAST_ID);
    }
  }, [missingUsername]);

  // One-shot toast on first network/parse error so the user knows what's wrong.
  useEffect(() => {
    if (query.isError && query.error) {
      toast.error("Couldn't resolve OCR account", {
        description:
          query.error instanceof Error
            ? query.error.message
            : String(query.error),
      });
    }
  }, [query.isError, query.error]);

  return {
    xUserId: query.data?.xUserId ?? null,
    // Callers see "resolving" until both the IAM name lookup AND the
    // /account POST have finished, so a spinner stays up for the full
    // chain instead of blinking off mid-way.
    isResolving:
      (orgNameQuery.isFetching && !orgNameQuery.data) ||
      (query.isFetching && !query.data),
    error: query.error instanceof Error ? query.error : null,
    gate,
  };
}
